<template>
  <SidebarMenu v-if="isVisibleRef" data-test-id="platform-setup-nav">
    <SidebarMenuItem>
      <Popover :open="isOpenRef" @update:open="(open) => (isOpenRef = open)">
        <PopoverTrigger as-child>
          <SidebarMenuButton
            size="lg"
            :tooltip="progressLabelRef"
            class="border border-primary/25 bg-sidebar-accent"
            data-test-id="platform-setup-nav-trigger"
          >
            <svg class="size-7! shrink-0" viewBox="0 0 28 28" aria-hidden="true">
              <circle cx="14" cy="14" r="11" fill="none" stroke="currentColor" stroke-opacity="0.15" stroke-width="4" />
              <circle
                cx="14"
                cy="14"
                r="11"
                fill="none"
                stroke="var(--primary)"
                stroke-width="4"
                stroke-linecap="round"
                :stroke-dasharray="`${ringRef} ${RING_LENGTH}`"
                transform="rotate(-90 14 14)"
              />
            </svg>
            <span class="flex min-w-0 flex-col">
              <span class="truncate text-sm font-semibold">{{ t('admin.setup.nav.title') }}</span>
              <span class="truncate text-xs text-muted-foreground">{{ progressRef }}</span>
            </span>
          </SidebarMenuButton>
        </PopoverTrigger>
        <PopoverContent
          side="right"
          align="end"
          class="flex w-72 flex-col gap-3"
          data-test-id="platform-setup-nav-popover"
        >
          <strong class="text-sm">{{
            t('admin.setup.nav.remaining', { count: todoRef.length }, todoRef.length)
          }}</strong>
          <ul class="flex flex-col gap-2">
            <li v-for="item in todoRef" :key="item.key">
              <router-link
                :to="{ path: ROUTES.ADMIN.SETUP, query: { step: item.step } }"
                class="flex items-center gap-2.5 rounded-md bg-muted px-2.5 py-2 text-sm font-medium text-foreground! no-underline! hover:bg-accent"
                @click="isOpenRef = false"
              >
                <span class="size-4 shrink-0 rounded-full border-2 border-input" aria-hidden="true" />
                <span class="flex-1">{{ t(`admin.setup.checklist.items.${item.key}`) }}</span>
                <ChevronRightIcon class="size-3.5 text-muted-foreground" aria-hidden="true" />
              </router-link>
            </li>
          </ul>
          <span v-if="doneLabelRef" class="text-xs text-muted-foreground">{{ doneLabelRef }}</span>
        </PopoverContent>
      </Popover>
    </SidebarMenuItem>
  </SidebarMenu>
</template>

<script setup lang="ts">
import { useQuery } from '@tanstack/vue-query'
import { ChevronRightIcon } from 'lucide-vue-next'
import { computed, onMounted, ref } from 'vue'

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem } from '@/components/ui/sidebar'
import { PLATFORM_SETTINGS_QUERY_KEY } from '@/features/admin/platform-setup/hooks/use-platform-setup.hook'
import usePlatformSetupStatus from '@/features/admin/platform-setup/hooks/use-platform-setup-status.hook'
import { computeChecklist, toSettingsMap } from '@/features/admin/platform-setup/utils/checklist'
import { consumeNavAutoOpen } from '@/features/admin/platform-setup/utils/dismissal'
import { settingsApi } from '@/features/admin/settings/api'
import { selectGetUserActivePermissionGroup } from '@/features/auth/stores/auth/auth.selectors'
import authStore from '@/features/auth/stores/auth/auth.store'
import { PermissionsGroupsEnumType } from '@/features/auth/types'
import { useI18n } from '@/lib/i18n'
import { ROUTES } from '@/utils/config/constants'

const RING_LENGTH = 2 * Math.PI * 11

const { t } = useI18n()
const { isPlatformAdminRef, isTrackedRef } = usePlatformSetupStatus()

const settingsQuery = useQuery({
  queryKey: [PLATFORM_SETTINGS_QUERY_KEY],
  queryFn: settingsApi.getAllSettings,
  enabled: computed(() => isPlatformAdminRef.value && isTrackedRef.value),
})

const itemsRef = computed(() => computeChecklist(toSettingsMap(settingsQuery.data.value)))
const todoRef = computed(() => itemsRef.value.filter((item) => !item.done))
const doneCountRef = computed(() => itemsRef.value.length - todoRef.value.length)
const ringRef = computed(() => (itemsRef.value.length ? (doneCountRef.value / itemsRef.value.length) * RING_LENGTH : 0))
const progressRef = computed(() =>
  t('admin.setup.nav.progress', { done: doneCountRef.value, total: itemsRef.value.length }),
)
const progressLabelRef = computed(() => `${t('admin.setup.nav.title')}, ${progressRef.value}`)
const doneLabelRef = computed(() => {
  const done = itemsRef.value.filter((item) => item.done).map((item) => t(`admin.setup.checklist.items.${item.key}`))
  return done.length ? t('admin.setup.nav.done', { items: done.join(', ') }) : ''
})

// Visible pour un admin, sur une plateforme créée avec l'assistant, tant qu'un point reste à faire.
const activePermissionGroupRef = selectGetUserActivePermissionGroup(authStore)
const isVisibleRef = computed(
  () =>
    activePermissionGroupRef.value === PermissionsGroupsEnumType.ADMIN &&
    isPlatformAdminRef.value &&
    isTrackedRef.value &&
    !!settingsQuery.data.value &&
    todoRef.value.length > 0,
)

const isOpenRef = ref(false)
// Juste après « Terminer » dans l'assistant, la liste s'ouvre une fois d'elle-même.
onMounted(() => {
  if (consumeNavAutoOpen()) isOpenRef.value = true
})
</script>
